import axios, { AxiosHeaders, AxiosInstance } from "axios";
import { joinUrl } from "../utils/url";
import { GATEWAY_BASE, SEC_13F_PATH, API_TIMEOUT_MS } from "../config/env";
import { notifyUnauthorized } from "../auth/unauthorizedHandler";

function apiBaseUrl(): string {
    return joinUrl(GATEWAY_BASE, SEC_13F_PATH);
}

export interface ApiClientOptions {
    timeoutMs?: number;
}

// Small helper: give the AuthProvider a moment to mount and publish its globals
async function waitForProviderBindings(): Promise<void> {
    for (let i = 0; i < 50; i++) {
        // ~500ms max
        if ((window as any).__rd_wait_for_refresh__ || (window as any).__rd_call_refresh__) return;
        await new Promise((r) => setTimeout(r, 10));
    }
}

export function createApiClient(opts: ApiClientOptions = {}): AxiosInstance {
    const baseURL = apiBaseUrl();
    const timeout = opts.timeoutMs ?? API_TIMEOUT_MS;

    const api = axios.create({ baseURL, timeout, withCredentials: true });

    // REQUEST: wait for boot refresh if one is running
    api.interceptors.request.use(async (config) => {
        config.headers = (config.headers ?? new AxiosHeaders()) as AxiosHeaders;

        // ensure provider had a chance to publish helpers
        await waitForProviderBindings();

        // if a refresh is already in flight, wait before sending the request
        const wait = (window as any).__rd_wait_for_refresh__ as undefined | (() => Promise<void>);
        if (typeof wait === "function") {
            try {
                await wait();
            } catch {
                /* ignore; response path will handle auth state */
            }
        }

        return config;
    });

    // RESPONSE: handle 401/429/5xx with smart retry
    api.interceptors.response.use(
        (r) => r,
        async (error) => {
            const cfg: any = error?.config ?? {};
            const status: number | undefined = error?.response?.status;
            const tries: number = cfg.__retryCount ?? 0;

            // First, try to self-heal 401 ONCE by asking AuthProvider to refresh, then replay
            if (status === 401 && !cfg.__retryAuth) {
                const refresh = (window as any).__rd_call_refresh__ as undefined | (() => Promise<boolean>);
                if (typeof refresh === "function") {
                    try {
                        const ok = await refresh();
                        if (ok) {
                            cfg.__retryAuth = true;
                            return api(cfg);
                        }
                    } catch {
                        /* fall through to logout */
                    }
                }
                // If refresh was unavailable or failed, log out and surface the error
                notifyUnauthorized();
                return Promise.reject(error);
            }

            // Gentle backoff for 429 and 5xx (max 2 retries)
            const canRetry =
                status !== undefined && (status === 429 || status >= 500) && tries < 2;

            if (canRetry) {
                await new Promise((res) => setTimeout(res, 400 * (tries + 1)));
                cfg.__retryCount = tries + 1;
                return api(cfg);
            }

            // default: just fail
            return Promise.reject(error);
        }
    );

    return api;
}
